import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, TrendingUp, Package, BarChart2, Zap, Users, ShoppingCart } from 'lucide-react';

const FMCG = () => {
  const useCases = [
    {
      icon: TrendingUp,
      title: 'Demand Forecasting & Inventory Optimisation',
      description: 'ML models that predict SKU-level demand across channels, reducing overstock and stockouts while optimising working capital.',
      outcomes: ['30–45% reduction in excess inventory', 'Improved service levels to 98%+', 'Automated replenishment triggers'],
    },
    {
      icon: Package,
      title: 'Supply Chain Visibility & Risk Intelligence',
      description: 'Real-time tracking of supplier performance, lead-time variance, and disruption signals — so you act before shortages hit shelves.',
      outcomes: ['End-to-end supply chain visibility', 'Early disruption alerts', 'Supplier risk scoring'],
    },
    {
      icon: ShoppingCart,
      title: 'Trade Promotion Optimisation',
      description: 'AI that evaluates historical promotion ROI, predicts uplift by retailer and region, and recommends optimal spend allocation.',
      outcomes: ['15–25% improvement in promo ROI', 'Reduced baseline cannibalisation', 'Post-promo wash-up in hours not weeks'],
    },
    {
      icon: BarChart2,
      title: 'Consumer & Market Analytics',
      description: 'Unified consumer data platforms that connect POS, loyalty, social, and panel data for real-time category and brand performance insights.',
      outcomes: ['360° consumer view', 'Real-time market share tracking', 'Automated insight generation'],
    },
    {
      icon: Zap,
      title: 'Route-to-Market & Salesforce AI',
      description: 'AI-powered territory planning, visit frequency optimisation, and outlet scoring that maximises field-force productivity.',
      outcomes: ['20% more productive sales visits', 'Data-driven outlet prioritisation', 'In-app AI coaching for reps'],
    },
    {
      icon: Users,
      title: 'Customer & Channel Analytics',
      description: 'Predictive churn models, next-best-action engines, and personalisation at scale across modern and traditional trade channels.',
      outcomes: ['Reduced customer churn', 'Personalised trade terms', 'Channel-level P&L visibility'],
    },
  ];

  const caseStudies = [
    {
      title: 'AI-Driven Demand Forecasting for Global FMCG',
      result: '35% reduction in excess inventory and 99% service-level attainment across 12 markets.',
      link: '/case-studies/manufacturing-ai',
    },
    {
      title: 'FMCG Data Platform Migration',
      result: 'Consolidated 14 data sources into a single analytics platform, cutting reporting time from days to minutes.',
      link: '/case-studies/fmcg-migration',
    },
  ];

  const techStack = [
    'Azure Synapse / Databricks', 'Power BI & Tableau', 'Python / XGBoost / LightGBM',
    'dbt for data transformation', 'Salesforce + custom AI overlays',
    'SAP integration & ERP connectors', 'Real-time POS data ingestion', 'GPT-4 for automated insight narratives',
  ];

  return (
    <>
      <Helmet>
        <title>AI for FMCG | Demand Forecasting, Supply Chain & Trade Promotion AI | Agility</title>
        <meta
          name="description"
          content="AI and data analytics solutions purpose-built for FMCG companies. Demand forecasting, inventory optimisation, trade promotion AI, supply chain visibility, and consumer analytics. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="FMCG AI, demand forecasting AI, trade promotion optimisation, supply chain analytics, consumer goods AI, FMCG data analytics, inventory optimisation AI"
        />
        <link rel="canonical" href="https://agility.ai/industries/fmcg" />
        <meta property="og:title" content="AI for FMCG | Agility" />
        <meta property="og:description" content="AI and data analytics solutions purpose-built for FMCG companies." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">FMCG</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              From shelf to supply chain, we build AI systems that help fast-moving consumer goods companies forecast better, promote smarter, and win at retail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to an FMCG AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              AI Use Cases for FMCG
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Proven AI applications across the full FMCG value chain — from factory gate to consumer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">FMCG Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to build AI into your FMCG operations?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            We deploy production-ready AI systems in 3–8 weeks. Let's map out your highest-ROI use case.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default FMCG;
